import equal from 'fast-deep-equal/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import styles from './RangeRewards.scss';

import type { IReward } from '~/types';
import type { RootState } from '~/store';

import { useAppSelector } from '~/hooks';
import RewardsDialog from '~/Components/Dialogs/RewardsDialog/RewardsDialog';
import RewardShip from '~/Components/RewardShip/RewardShip';
import { getShipIdFromReward } from '~/utils/rewards';
import RewardCount from '~/Components/Reward/RewardCount';

export interface RangeRewards_Props {
    from: number,
    to: number,
    filteredShips: Array<string>,
    isLastIncluded: boolean,
}

export interface RangeRewardsState {
    rewards: Array<IReward>,
}

const MAX_VISIBLE_COUNT = 7;

const stateSelector = (state: RootState): RangeRewardsState => {
    return {
        rewards: state.app.rewards,
    };
};

const RangeRewards = (props: RangeRewards_Props) => {
    const {
        from, to, filteredShips, isLastIncluded
    } = props;

    const { t } = useTranslation();
    const { rewards } = useAppSelector(stateSelector, equal);
    const [isRestRewardsDialogVisible, setIsRestRewardsDialogVisible] = useState(false);

    const showRewardsDialog = useCallback(() => {
        setIsRestRewardsDialogVisible(true);
    }, []);

    const hideRewardsDialog = useCallback(() => {
        setIsRestRewardsDialogVisible(false);
    }, []);

    const renderReward = (reward: IReward) => {
        const shipId = getShipIdFromReward(reward);

        if (shipId) {
            return <RewardShip id={shipId} fixedTooltip={false} reward={reward} />;
        }
        return <RewardCount reward={reward} />;
    };

    const visibleItemsArray: Array<IReward> = [];

    const renderRewards = (rewardsArray: Array<IReward>, isShip: boolean) => {
        const icons: Array<React.ReactNode> = [];

        const filteredRewards = rewardsArray.filter((reward) => {
            const shipId = getShipIdFromReward(reward);
            const isShipReward = !!shipId;
            return isShip === isShipReward
                && reward.gameRewards
                && reward.gameRewards.length > 0
                && !filteredShips.includes(shipId as string);
        });

        filteredRewards.forEach((reward: IReward, index: number) => {
            const isVisible = reward.shipLevelNumber <= to
                && reward.shipLevelNumber > from
                && visibleItemsArray.length < MAX_VISIBLE_COUNT;

            if (isVisible) {
                visibleItemsArray.push(reward);
            }
            icons.push(
                <CSSTransition
                    classNames="zoom-range-reward"
                    in={isVisible}
                    timeout={300}
                    unmountOnExit
                    key={`RangeReward-${index}`}
                >
                    <div className={styles.rewardWrapper} key={`reward-${reward.id}`}>
                        {renderReward(reward)}
                    </div>
                </CSSTransition>,
            );
        });

        return icons;
    };

    const ships = renderRewards(rewards, true);
    const restRewards = renderRewards(rewards, false);
    let hiddenRewardsCount = to - from - visibleItemsArray.length;
    if (isLastIncluded) {
        hiddenRewardsCount -= 1;
    }
    const isRestRewardsButtonVisible = hiddenRewardsCount > 0;
    const restRewardsIds = rewards.filter((r) => r.shipLevelNumber > from && r.shipLevelNumber <= to).map((r) => r.id);

    const renderRestRewardsCountButton = () => {
        return (
            <CSSTransition
                classNames="zoom-range-reward"
                in={isRestRewardsButtonVisible}
                timeout={200}
                unmountOnExit
                key={'CountButton'}
            >
                <div className={styles.showRewardsDialogButton} onClick={showRewardsDialog}>
                    {`+${hiddenRewardsCount}`}
                </div>
            </CSSTransition>
        );
    };

    return (
        <>
            <div className={styles.wrapper}>
                <div className={styles.availableRewardsIcons}>
                    {ships}
                    {restRewards}
                    {renderRestRewardsCountButton()}
                </div>
            </div>
            <RewardsDialog
                isVisible={isRestRewardsDialogVisible}
                ids={restRewardsIds}
                title={t('Награды')}
                actionButtonText={t('Закрыть')}
                onHide={hideRewardsDialog}
                onActionButtonPress={hideRewardsDialog}
            />
        </>
    );
};

export default RangeRewards;
